import { formatDate, formatUZS, type ProductListItem } from "@elektr/shared";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Building2,
  ClipboardList,
  Database,
  FileEdit,
  HardHat,
  Package,
  Plus,
  QrCode,
  Users,
} from "lucide-react";
import type { ComponentType } from "react";
import { Link } from "react-router-dom";

import { Badge, Card, Spinner } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Hero } from "@/components/ui/hero";
import { api, unwrap } from "@/lib/api";
import { isAdmin, useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { STATUS_KEYS, STATUS_TONE } from "@/lib/labels";
import { useCountUp } from "@/lib/useCountUp";
import { cn } from "@/lib/utils";

function useCount(key: string, fn: () => Promise<{ count: number }>, enabled = true) {
  return useQuery({ queryKey: ["count", key], queryFn: fn, enabled, select: (d) => d.count });
}

const TONES = [
  "from-violet-500 to-indigo-500",
  "from-emerald-400 to-teal-500",
  "from-amber-400 to-orange-500",
  "from-sky-400 to-blue-500",
  "from-pink-400 to-rose-500",
] as const;

function Stat({
  icon: Icon,
  label,
  value,
  to,
  tone,
  index,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value?: number;
  to?: string;
  tone: number;
  index: number;
}) {
  const shown = useCountUp(value);
  const body = (
    <Card
      className="anim-fade-up group flex items-center gap-4 transition-all duration-200 hover:-translate-y-1 hover:shadow-pop"
      style={{ ["--i" as string]: index + 2 }}
    >
      <div
        className={cn(
          "rounded-2xl bg-gradient-to-br p-3.5 text-white shadow-lg transition-transform duration-200 group-hover:scale-110 group-hover:-rotate-6",
          TONES[tone % TONES.length],
        )}
      >
        <Icon className="size-6" />
      </div>
      <div>
        <div className="text-3xl font-extrabold tabular-nums leading-none">
          {shown ?? <span className="skeleton inline-block h-7 w-10 rounded-md align-middle" />}
        </div>
        <div className="mt-1 text-sm text-muted-foreground">{label}</div>
      </div>
    </Card>
  );
  return to ? (
    <Link to={to} className="block">
      {body}
    </Link>
  ) : (
    body
  );
}

function QuickAction({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="flex min-h-11 items-center gap-2 rounded-xl bg-white/15 px-4 text-sm font-semibold text-white ring-1 ring-white/25 backdrop-blur transition hover:bg-white/25 active:scale-[0.97]"
    >
      <Icon className="size-4" /> {label}
    </Link>
  );
}

function ProductCard({ p, index }: { p: ProductListItem; index: number }) {
  return (
    <Link
      to={`/catalog/${p.id}`}
      className="anim-fade-up shadow-card group w-44 shrink-0 snap-start overflow-hidden rounded-2xl border border-border bg-card transition-all duration-200 hover:-translate-y-1 hover:shadow-pop sm:w-52"
      style={{ ["--i" as string]: index }}
    >
      <div className="aspect-square overflow-hidden bg-muted">
        {p.cover_url ? (
          <img
            src={p.cover_url}
            alt={p.name}
            loading="lazy"
            className="size-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="flex size-full items-center justify-center text-muted-foreground">
            <Package className="size-10" />
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="truncate text-xs text-muted-foreground">{p.brand || p.category_name}</div>
        <div className="line-clamp-2 min-h-10 text-sm font-semibold leading-snug">{p.name}</div>
        <div className="mt-1 font-extrabold text-primary">{formatUZS(p.price_uzs)}</div>
      </div>
    </Link>
  );
}

function StorageRing({ percent, used, free }: { percent: number; used: number; free: number }) {
  const r = 38;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, percent));
  const tone = pct >= 100 ? "var(--danger)" : pct >= 80 ? "var(--warning)" : "var(--success)";
  return (
    <div className="relative size-24 shrink-0">
      <svg viewBox="0 0 100 100" className="size-full -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="var(--muted)" strokeWidth="10" />
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke={tone}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct / 100)}
          style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(.2,.8,.2,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-tight">
        <span className="text-lg font-extrabold tabular-nums">{Math.round(pct)}%</span>
        <span className="text-[10px] text-muted-foreground">
          {used.toFixed(1)}/{free} GB
        </span>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { t } = useT();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const superAdmin = user?.role === "super_admin";

  const props = (status?: "draft" | "ready") => async () =>
    unwrap(await api.GET("/api/properties/", { params: { query: { page_size: 1, status } } }));
  const users = (role: string) => async () =>
    unwrap(await api.GET("/api/users/", { params: { query: { page_size: 1, role } } }));

  const total = useCount("p-all", props());
  const ready = useCount("p-ready", props("ready"));
  const draft = useCount("p-draft", props("draft"));
  const customers = useCount("u-customer", users("customer"), admin);
  const electricians = useCount("u-electrician", users("electrician"), admin);

  const recent = useQuery({
    queryKey: ["properties", "recent"],
    queryFn: async () =>
      unwrap(await api.GET("/api/properties/", { params: { query: { page_size: 5 } } })),
  });
  const products = useQuery({
    queryKey: ["dash-products"],
    queryFn: async () =>
      unwrap(await api.GET("/api/products/", { params: { query: { page_size: 10 } } })),
    staleTime: 60_000,
  });
  const storage = useQuery({
    queryKey: ["object-storage"],
    queryFn: async () => unwrap(await api.GET("/api/hosting/object-storage/")),
    enabled: superAdmin,
    staleTime: 60_000,
  });

  const firstName = (user?.full_name || "").split(" ")[0] || user?.phone || "";
  const showcase = (products.data?.results ?? []).filter((p) => p.cover_url).slice(0, 10);

  return (
    <div className="flex flex-col gap-6">
      <Hero
        title={t("dash.greeting", { name: firstName })}
        subtitle={t("dash.hero_sub")}
        actions={
          <>
            <QuickAction to="/properties/new" icon={Plus} label={t("props.new")} />
            <QuickAction to="/properties" icon={QrCode} label={t("dash.quick_qr")} />
            <QuickAction to="/catalog" icon={Package} label={t("nav.catalog")} />
          </>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Stat
          icon={Building2}
          label={t("dash.properties_total")}
          value={total.data}
          to="/properties"
          tone={0}
          index={0}
        />
        <Stat
          icon={ClipboardList}
          label={t("dash.properties_ready")}
          value={ready.data}
          to="/properties?status=ready"
          tone={1}
          index={1}
        />
        <Stat
          icon={FileEdit}
          label={t("dash.properties_draft")}
          value={draft.data}
          to="/properties?status=draft"
          tone={2}
          index={2}
        />
        {admin && (
          <Stat
            icon={Users}
            label={t("nav.customers")}
            value={customers.data}
            to="/customers"
            tone={3}
            index={3}
          />
        )}
        {admin && (
          <Stat
            icon={HardHat}
            label={t("nav.electricians")}
            value={electricians.data}
            to="/electricians"
            tone={4}
            index={4}
          />
        )}
        {superAdmin && storage.data?.configured && (
          <Link to="/server" className="block">
            <Card
              className="anim-fade-up flex items-center gap-4 transition-all duration-200 hover:-translate-y-1 hover:shadow-pop"
              style={{ ["--i" as string]: 7 }}
            >
              <StorageRing
                percent={storage.data.percent}
                used={storage.data.used_gb}
                free={storage.data.free_gb}
              />
              <div>
                <div className="flex items-center gap-1.5 font-bold">
                  <Database className="size-4 text-primary" /> {t("ostore.title")}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">{t("dash.storage_hint")}</div>
              </div>
            </Card>
          </Link>
        )}
      </div>

      {showcase.length > 0 && (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-extrabold">{t("dash.showcase")}</h2>
            <Link
              to="/catalog"
              className="flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
            >
              {t("dash.see_all")} <ArrowRight className="size-4" />
            </Link>
          </div>
          <div className="hide-scrollbar -mx-3 flex snap-x gap-3 overflow-x-auto px-3 pb-3 md:mx-0 md:px-0">
            {showcase.map((p, i) => (
              <ProductCard key={p.id} p={p} index={i} />
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-extrabold">{t("dash.recent")}</h2>
          <Button asChild variant="ghost" size="sm">
            <Link to="/properties">
              {t("dash.see_all")} <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
        <div className="flex flex-col gap-2">
          {recent.isLoading && (
            <div className="p-6 text-center">
              <Spinner />
            </div>
          )}
          {recent.data?.results.map((p, i) => (
            <Link
              key={p.id}
              to={`/properties/${p.id}`}
              className="anim-fade-up shadow-card group flex items-center gap-3 rounded-2xl border border-border bg-card p-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40"
              style={{ ["--i" as string]: i }}
            >
              <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500/15 to-indigo-500/15 text-primary transition-transform group-hover:scale-105">
                <Building2 className="size-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">
                  {p.address} {p.house_number}
                </div>
                <div className="text-sm text-muted-foreground">
                  {p.district} · {formatDate(p.created_at)}
                </div>
              </div>
              <Badge tone={STATUS_TONE[p.status ?? "draft"]}>
                {t(STATUS_KEYS[p.status ?? "draft"])}
              </Badge>
            </Link>
          ))}
          {recent.data?.results.length === 0 && (
            <Card className="text-center text-muted-foreground">{t("props.empty")}</Card>
          )}
        </div>
      </section>
    </div>
  );
}
